import { getErrorMessage } from '@monetize/utils/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSetAtom } from 'jotai';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetTenantAndAutoAcceptInvite } from '../../../api/tenantService';
import AppLoading from '../../../components/App/AppLoading';
import { MLink } from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import { useQuery } from '../../../hooks';
import { useAuth } from '../../../services/auth0';
import Sentry from '../../../services/sentry';
import { useToast } from '../../../services/toast';
import {
  switchTenantVisibleState,
  tableFiltersState,
  tablePagerState,
} from '../../../store/global.store';
import { PersistKeysEnum, StorageOptions } from '../../../store/store.types';
import {
  clearBrowserStorage,
  forgetLastTenant,
} from '../../../store/store.utils';
import { useDashboardCurrencyFilter } from '../../Dashboard/components/useDashboardCurrencyFilter';
import { useNavigateToErrorPage } from '../../ErrorPages/ErrorPage';

const SwitchTenant = () => {
  const { addToast } = useToast();
  const setSwitchTenantVisible = useSetAtom(switchTenantVisibleState);
  const { selectTenant } = useAuth();
  const query = useQuery();
  const navigate = useNavigate();
  const tenantId = query.get('tenantId') || '';
  const redirectUrl = query.get('redirectUrl');
  const navigateToErrorPage = useNavigateToErrorPage();

  const onOpenSwitch = () => {
    setSwitchTenantVisible(true);
  };
  const resetTableFilters = useSetAtom(tableFiltersState);
  const resetTablePagers = useSetAtom(tablePagerState);
  const { setCurrency: resetDashboardCurrencyFilter } =
    useDashboardCurrencyFilter();
  const queryClient = useQueryClient();
  const { mutateAsync: getTenantAndAutoAcceptInvite } =
    useGetTenantAndAutoAcceptInvite(
      {
        onAcceptFailure: (ex) => {
          Sentry.logger.error('SwitchTenant - invite acceptance failed', {
            error: getErrorMessage(ex),
          });
          Sentry.captureException(ex, { tags: { type: 'TENANT_INVITE' } });
          forgetLastTenant(tenantId);
          navigateToErrorPage(
            'There was an error accepting your invite.',
            true,
          );
        },
      },
      {
        onError: (ex) => {
          Sentry.logger.error('SwitchTenant - tenant fetch failed', {
            error: getErrorMessage(ex),
          });
          // Don't auto-restore a tenant we could not switch into
          forgetLastTenant(tenantId);
          handleApiErrorToast(ex);
          navigate(ROUTES.ROOT);
        },
        onSuccess: (tenant) => {
          if (!tenant) {
            return;
          }
          Sentry.logger.info('SwitchTenant - tenant switched successfully');
          selectTenant(tenant);
          addToast({
            summary: `You are in ${tenant.name}`,
            summaryProps: { fontWeight: 'normal' },
            severity: 'info',
            customContent: (
              <MLink textDecor="underline" fontSize="xs" onClick={onOpenSwitch}>
                Change
              </MLink>
            ),
          });
          clearBrowserStorage({
            persistKey: PersistKeysEnum.TABLE_FILTERS,
            type: StorageOptions.SESSION,
          });
          clearBrowserStorage({
            persistKey: PersistKeysEnum.TABLE_PAGERS,
            type: StorageOptions.SESSION,
          });
          clearBrowserStorage({
            persistKey: PersistKeysEnum.DASHBOARD_CURRENCY_FILTER,
            type: StorageOptions.SESSION,
          });
          resetTableFilters({});
          resetTablePagers({});
          resetDashboardCurrencyFilter('');
          queryClient.clear();

          // Redirect to dashboard or included redirectUrl
          if (redirectUrl && !redirectUrl.includes('auth/')) {
            navigate(redirectUrl);
          } else {
            navigate(ROUTES.DASHBOARD);
          }
        },
      },
    );

  useEffect(() => {
    if (!tenantId) {
      // select tenant route if tenantId is empty
      navigate(ROUTES.ONBOARD_SELECT_TENANT);
    } else {
      getTenantAndAutoAcceptInvite({ id: tenantId });
    }
  }, [tenantId]);

  return <AppLoading loadingText="Validating the tenant..." />;
};

export default SwitchTenant;
